/**
 * The complete-states set (docs/03 §3): empty, error, denied, NOT-YET, and
 * NOT-APPLICABLE. Every list, record, and panel ships with all of them — these
 * are the shared renderings.
 */

import { Ban, Construction, Inbox, Lock, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { RouterAnchor } from '../../app/RouterAnchor.js';

import { Button } from './Button.js';

function StateShell({
  icon,
  title,
  body,
  action,
}: {
  readonly icon: ReactNode;
  readonly title: string;
  readonly body: string;
  readonly action?: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col items-center gap-3 rounded-card border border-card-line bg-card px-6 py-12 text-center">
      <span aria-hidden className="text-ink3">
        {icon}
      </span>
      <p className="font-display text-h2 text-ink">{title}</p>
      <p className="max-w-md text-body text-ink2">{body}</p>
      {action}
    </div>
  );
}

/** Empty states invite (docs/04 §2 copy rules): "Add your first vendor". */
export function EmptyState({
  title,
  body,
  cta,
  onCta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: string;
  readonly onCta?: () => void;
}): ReactNode {
  return (
    <StateShell
      icon={<Inbox size={32} aria-hidden />}
      title={title}
      body={body}
      action={
        cta !== undefined ? (
          <Button variant="primary" onClick={onCta}>
            {cta}
          </Button>
        ) : undefined
      }
    />
  );
}

/**
 * NOT-YET — a surface whose feature has not been built, which is NOT an empty
 * state (docs/03 §3).
 *
 * An empty state is a finding: we looked, there is nothing. A not-yet surface
 * has not looked. Rendering "Nothing overdue" from a lane with no query behind
 * it makes the app assert the one thing it cannot know, and the reader has no
 * way to tell that claim from a real one. The dashboard and every /work lane
 * did exactly this before O-28.
 *
 * Dashed border, no icon-in-a-card weight, no CTA: it reads as scaffolding
 * rather than as an answer.
 */
export function NotYetState({ body }: { readonly body: string }): ReactNode {
  return (
    <div className="flex flex-col items-center gap-2 rounded-card border border-dashed border-control-line px-6 py-8 text-center">
      <span aria-hidden className="text-ink3">
        <Construction size={24} aria-hidden />
      </span>
      <p className="text-body font-medium text-ink">Not turned on yet</p>
      <p className="max-w-prose text-meta text-ink2">{body}</p>
    </div>
  );
}

/** The standard denied state (docs/03 §4) — never a blank screen. */
export function DeniedState({
  requestAccessEmail,
}: {
  readonly requestAccessEmail?: string;
}): ReactNode {
  return (
    <StateShell
      icon={<Lock size={32} aria-hidden />}
      title="You don't have access to this"
      body="Your role doesn't include this area. If you need it for your work, ask an administrator."
      action={
        requestAccessEmail !== undefined ? (
          <a
            className="text-body font-medium text-brand-700 underline"
            href={`mailto:${requestAccessEmail}?subject=PropOS access request`}
          >
            Request access
          </a>
        ) : undefined
      }
    />
  );
}

/**
 * NOT APPLICABLE — this surface cannot exist for THIS record (docs/04 §3d).
 *
 * Built for mailbox RM-053: hiding a tab is not a workflow boundary. A service
 * vendor's `/accounts` URL, a utility vendor's bare `/properties` URL and
 * `/agreements/new?vendor=<utility-id>` all rendered fully operable screens with
 * copy for the wrong kind of vendor, because bookmarks, history, copied links
 * and stale internal links reach a route whether or not a tab points at it.
 *
 * Distinct from every neighbouring state, and each difference is load-bearing:
 * `EmptyState` says "nothing here YET" and invites you to add the first one,
 * which is a lie when the record can never hold one; `DeniedState` says the
 * problem is your role, when a different role would not help either;
 * `ErrorState` says something went wrong, when nothing did. So it says what is
 * true — this does not apply to this record — and, unlike the silent redirect
 * it replaces, it says WHY and offers the place that does apply. A redirect to
 * the dashboard loses both the explanation and the record.
 *
 * `where` is a LINK, not a button: the destination is a place, so it must be
 * middle-clickable, copyable and in the history like any other navigation.
 */
export function NotApplicableState({
  title,
  body,
  where,
}: {
  readonly title: string;
  readonly body: string;
  /** The valid equivalent, or simply the record this surface hangs off. */
  readonly where: { readonly label: string; readonly href: string };
}): ReactNode {
  return (
    <StateShell
      icon={<Ban size={32} aria-hidden />}
      title={title}
      body={body}
      action={
        <RouterAnchor className="text-body font-medium text-brand-700 underline" href={where.href}>
          {where.label}
        </RouterAnchor>
      }
    />
  );
}

/** Error states say what happened and what to do (docs/03 §3). */
export function ErrorState({
  title = 'Something went wrong',
  body,
  onRetry,
}: {
  readonly title?: string | undefined;
  readonly body: string;
  readonly onRetry?: (() => void) | undefined;
}): ReactNode {
  return (
    <StateShell
      icon={<TriangleAlert size={32} aria-hidden />}
      title={title}
      body={body}
      action={onRetry !== undefined ? <Button onClick={onRetry}>Try again</Button> : undefined}
    />
  );
}
